<script>
import { sentimentMeta } from '@modules/crm/constants/Sentiment'
import TranscriptRingGauge from '@modules/crm/components/transcript-ring-gauge/transcript-ring-gauge.vue'

// Hex equivalentes aos tokens de sentimento (o stroke do SVG precisa de hex).
const SENTIMENT_HEX = {
  positivo: '#56AF79',
  neutro: '#E0AF00',
  negativo: '#EB5757',
}

const ENGAJAMENTO_LABELS = {
  alto: 'Alto',
  medio: 'Médio',
  baixo: 'Baixo',
}

export default {
  name: 'TranscriptSentimentGauge',
  components: { TranscriptRingGauge },
  props: {
    sentimento: { type: String, default: null },
    score: { type: Number, default: null },
    // Nível de engajamento da reunião (alto/medio/baixo) — segunda linha
    // rotulada ("Engajamento: …") ao lado/abaixo do nome do sentimento.
    engajamento: { type: String, default: null },
    // Diâmetro do anel em px (header usa pequeno; aba Sentimento usa grande).
    size: { type: Number, default: 48 },
    // Mostra "/100" abaixo do número (versão grande da aba de Sentimento).
    showScale: { type: Boolean, default: false },
  },
  computed: {
    // Há análise de sentimento? Sem ela, o componente mostra um estado vazio
    // (anel cinza + "Sem análise") em vez do score.
    hasSentiment() {
      return Boolean(this.sentimento)
    },
    meta() {
      return sentimentMeta(this.sentimento)
    },
    sentimentoHex() {
      return SENTIMENT_HEX[this.sentimento] || '#BEBEBE'
    },
    // Nome exibido ao lado do anel: rótulo do sentimento ou estado vazio.
    displayLabel() {
      return this.hasSentiment ? this.meta.label : 'Sem análise'
    },
    labelColor() {
      return this.hasSentiment ? this.sentimentoHex : '#8E8E8E'
    },
    // Trilha clara do anel derivada do hex do sentimento.
    sentimentoTint() {
      return 'color-mix(in srgb, ' + this.sentimentoHex + ' 15%, #FFFFFF)'
    },
    // Score normalizado para 0-100 (backend pode mandar 0-1 ou 0-100).
    nota() {
      const raw = this.score
      if (raw === null || raw === undefined) return null
      const num = Number(raw)
      if (Number.isNaN(num)) return null
      return num <= 1 ? Math.round(num * 100) : Math.round(num)
    },
    // Valor passado ao ring gauge (escala 0-100): 0 mantém o anel vazio.
    ringValue() {
      return this.nota || 0
    },
    engajamentoLabel() {
      return (this.engajamento && ENGAJAMENTO_LABELS[this.engajamento]) || ''
    },
    // Tamanho da fonte do número central proporcional ao diâmetro do anel.
    valueStyle() {
      return { fontSize: Math.round(this.size * 0.3) + 'px' }
    },
    // Ícone de "sem análise" (mdi-help-circle) proporcional ao anel.
    emptyIconSize() {
      return Math.round(this.size * 0.42)
    },
  },
}
</script>

<template>
  <div class="d-flex align-center az-gap-small">
    <!-- Anel genérico preenchido conforme o score, na cor do sentimento -->
    <TranscriptRingGauge
      :value="ringValue"
      :max="100"
      :color="sentimentoHex"
      :track-color="sentimentoTint"
      :size="size"
    >
      <span
        v-if="hasSentiment"
        class="transcript-sentiment-gauge__value az-text-primary"
        :style="valueStyle"
      >
        {{ nota !== null ? nota : '-' }}
      </span>
      <span v-else class="transcript-sentiment-gauge__value">
        <icon-svg name="mdi-help-circle" :size="emptyIconSize" color="#BEBEBE" />
      </span>
    </TranscriptRingGauge>

    <!-- Nome do sentimento (na cor dele) + nível de engajamento -->
    <div class="d-flex flex-column" style="min-width: 0">
      <span
        class="font-semibold text-truncate"
        :class="showScale ? 'az-size-16' : 'az-size-14'"
        :style="{ color: labelColor }"
      >
        {{ displayLabel }}
      </span>
      <span v-if="showScale && hasSentiment" class="az-text-small">
        Score: {{ nota }}/100
      </span>
      <span v-if="engajamentoLabel" class="az-text-small text-truncate">
        Engajamento: {{ engajamentoLabel }}
      </span>
      <span v-if="!hasSentiment" class="az-text-small">
        Sentimento ainda não disponível
      </span>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// Apenas tipografia do número central — o anel e o posicionamento do slot vêm
// do TranscriptRingGauge.
.transcript-sentiment-gauge
  &__value
    display: flex
    align-items: center
    justify-content: center
    font-weight: 700
    line-height: 1
</style>
